/**
 * The sounds of one audio context, made on first use and kept: plucked strings sampled every three
 * semitones (and played back a little faster or slower for the notes between), qin harmonics, the
 * percussion, the breath noise and the hall, and the wave shapes of the held instruments.
 */
import { loadRecordedZheng, recordedZheng, stringFor } from './guzheng';
import { drum, hall, harmonic, noise, pluck, PLUCKS, type PluckKind } from './samples';
import { mtof } from './theory';

/** Strings are sampled every this many semitones. */
const ANCHOR = 3;

export interface Sample {
  readonly buffer: AudioBuffer;
  /** Playback rate that brings the sample to the wanted pitch. */
  readonly rate: number;
  /** A recorded string (guzheng.ts), not a synthesized one: it already has its own attack. */
  readonly recorded?: boolean;
}

export type HeldKind = 'dizi' | 'xiao' | 'erhu' | 'pad';

/** Harmonic spectra of the held voices (sine amplitudes, from the fundamental up). */
const SPECTRA: Record<HeldKind, readonly number[]> = {
  // 二胡: a bowed, nasal tone, its third to fifth harmonics strong.
  erhu: [1, 0.72, 0.78, 0.66, 0.55, 0.38, 0.3, 0.24, 0.18, 0.15, 0.11, 0.09, 0.07, 0.05],
  // 笛子: a clear flute, with the buzz of the 笛膜 membrane high up.
  dizi: [1, 0.42, 0.24, 0.1, 0.06, 0.07, 0.07, 0.06, 0.05, 0.045, 0.04, 0.03],
  // 箫: soft and hollow, nearly a pure tone.
  xiao: [1, 0.2, 0.09, 0.03, 0.012],
  // 笙-like reed pad, softened further by its channel's low-pass.
  pad: [1, 0.55, 0.4, 0.28, 0.2, 0.14, 0.1, 0.07],
};

export class SampleBank {
  private readonly plucks = new Map<string, AudioBuffer>();
  private readonly drums = new Map<string, AudioBuffer>();
  private readonly waves = new Map<HeldKind, PeriodicWave>();
  private noiseBuf: AudioBuffer | null = null;
  private hallBuf: AudioBuffer | null = null;

  /**
   * Rate the strings and drums are made at: 32 kHz is plenty for them (the playback resamples), and
   * it keeps the memory and the time to make them down.
   */
  readonly sr: number;
  /** Sounds still to make before they are needed, a few at a time (`idle`). */
  private readonly queue: (() => void)[] = [];

  constructor(readonly ctx: BaseAudioContext) {
    this.sr = Math.min(ctx.sampleRate, 32000);
    // The recorded zheng: fetched once for every bank, used as soon as it is in.
    void loadRecordedZheng();
  }

  private buffer(data: Float32Array, sr = this.sr): AudioBuffer {
    const b = this.ctx.createBuffer(1, data.length, sr);
    b.copyToChannel(data as Float32Array<ArrayBuffer>, 0);
    return b;
  }

  /** A plucked note (or a qin harmonic) at a MIDI pitch. */
  pluck(kind: PluckKind, midi: number, harm = false): Sample {
    const real = kind === 'zheng' && !harm ? recordedZheng() : null;
    if (real) {
      const s = stringFor(real, midi);
      const key = `zheng@${s.midi}`;
      let b = this.plucks.get(key);
      if (!b) {
        b = this.buffer(s.data, s.sampleRate);
        this.plucks.set(key, b);
      }
      return { buffer: b, rate: 2 ** ((midi - s.midi + s.tune) / 12), recorded: true };
    }
    const anchor = Math.round(midi / ANCHOR) * ANCHOR;
    const key = `${kind}${harm ? '*' : ''}${anchor}`;
    let b = this.plucks.get(key);
    if (!b) {
      const sr = this.sr;
      const seed = anchor * 131 + kind.length * 7 + (harm ? 5 : 0);
      b = this.buffer(harm ? harmonic(sr, mtof(anchor), seed) : pluck(sr, mtof(anchor), PLUCKS[kind], seed));
      this.plucks.set(key, b);
    }
    return { buffer: b, rate: 2 ** ((midi - anchor) / 12) };
  }

  drum(letter: string): AudioBuffer {
    let b = this.drums.get(letter);
    if (!b) {
      b = this.buffer(drum(this.sr, letter));
      this.drums.set(letter, b);
    }
    return b;
  }

  /**
   * Queues every sound a track uses, to be made in idle moments before it plays them; `first`: ahead
   * of everything queued so far (the track about to play).
   */
  want(
    events: Iterable<{ readonly inst: string; readonly midi: number; readonly drum?: string; readonly techs: readonly string[] }>,
    first = false,
  ): void {
    const jobs: (() => void)[] = [];
    const seen = new Set<string>();
    for (const e of events) {
      const pl = e.inst === 'zheng' || e.inst === 'qin' || e.inst === 'pipa';
      if (!pl && !e.drum) continue;
      const harm = e.techs.includes('fan');
      // The recorded zheng needs nothing made.
      if (e.inst === 'zheng' && !harm && recordedZheng()) continue;
      const key = e.drum ?? `${e.inst}${harm ? '*' : ''}${Math.round(e.midi / ANCHOR) * ANCHOR}`;
      if (seen.has(key) || this.plucks.has(key) || this.drums.has(key)) continue;
      seen.add(key);
      jobs.push(e.drum ? () => this.drum(e.drum!) : () => this.pluck(e.inst as PluckKind, e.midi, harm));
    }
    if (first) this.queue.unshift(...jobs);
    else this.queue.push(...jobs);
  }

  /** Makes queued sounds for up to `ms` milliseconds. */
  idle(ms: number): void {
    const end = performance.now() + ms;
    while (this.queue.length && performance.now() < end) this.queue.shift()!();
  }

  wave(kind: HeldKind): PeriodicWave {
    let w = this.waves.get(kind);
    if (!w) {
      const amps = SPECTRA[kind];
      const real = new Float32Array(amps.length + 1);
      const imag = new Float32Array(amps.length + 1);
      amps.forEach((a, i) => (imag[i + 1] = a));
      w = this.ctx.createPeriodicWave(real, imag);
      this.waves.set(kind, w);
    }
    return w;
  }

  /** Three seconds of noise, looped for the flutes' breath. */
  noise(): AudioBuffer {
    return (this.noiseBuf ??= this.buffer(noise(this.sr, 3, 4242)));
  }

  /** The hall's echo, in stereo. */
  hall(): AudioBuffer {
    if (!this.hallBuf) {
      const [l, r] = hall(this.ctx.sampleRate, 3.2, 777);
      const b = this.ctx.createBuffer(2, l.length, this.ctx.sampleRate);
      b.copyToChannel(l as Float32Array<ArrayBuffer>, 0);
      b.copyToChannel(r as Float32Array<ArrayBuffer>, 1);
      this.hallBuf = b;
    }
    return this.hallBuf;
  }
}
